import React, { useRef, useState } from 'react';
import { CssBaseline, Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ContractDetail from '../pages/ContractDetail';
import Footer from '../components/Footer';
import GlobalNavbar from '../components/GlobalNavbar';
import { createAppTheme } from '../theme';
import { ColorModeProvider } from '../theme/ColorModeContext';

const flowId = 'contract-schedule-e2e';
const longTitle = 'Contract administration plan review for the long-running enterprise modernization and support transition';

const optionPeriods = Array.from({ length: 12 }, (_, index) => {
  const year = 2026 + index;
  return {
    option_period_id: index === 0 ? 'base' : `oy${index}`,
    label: index === 0
      ? 'Base period with an intentionally descriptive operational label'
      : `Option Year ${index} — enterprise service continuity and transition support`,
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
    exercise_status: index === 0 ? 'current' : 'planned',
    reminders: [],
  };
});

const suspenses = optionPeriods.slice(1).map((period, index) => ({
  event_key: `notice-${index + 1}`,
  option_period_id: period.option_period_id,
  contract_number: 'FA-E2E-2026-0001',
  contract_name: 'Enterprise modernization and support transition',
  title: `${longTitle} ${index + 1}`,
  type: 'option_exercise',
  owner: 'Contracting Officer with a deliberately long deterministic owner name for containment coverage',
  due_date: `${2026 + index}-10-01`,
  days_remaining: 30,
  status: 'upcoming',
  urgency: index % 2 === 0 ? 'yellow' : 'orange',
}));

const schedule = {
  flow_id: flowId,
  display: {
    workflow_name: 'Enterprise modernization schedule fixture',
    contract_number: 'FA-E2E-2026-0001',
    contract_name: 'Enterprise modernization and support transition',
  },
  pre_award_milestones: [
    {
      id: 'acquisition-plan', label: 'Acquisition strategy and independent cost estimate review', workflow_order: 1,
      status: 'complete', assignee: 'Program manager with a long deterministic fixture name',
      planned_start_date: '2025-01-01', planned_end_date: '2025-06-30', target_date: null,
      actual_occurred_at: '2025-06-15T12:00:00Z', actual_source_action_id: 'fixture-action',
      recorded_by: 'fixture@example.test', recorded_at: '2025-06-15T12:00:00Z', provenance: 'fixture',
    },
  ],
  option_periods: optionPeriods,
  suspenses,
  activity_events: [
    {
      event_id: 'fixture-action', activity_type: 'business_clearance', status: 'completed',
      occurred_at: '2024-12-15T23:30:00-05:00', performed_by: 'Fixture program manager', recorded_by: 'fixture@example.test',
      performed_by_role: null, workflow_stage: '615', artifact_link: null, note: null, updated_at: null, updated_by: null,
    },
    {
      event_id: 'fixture-july-last', activity_type: 'last review', status: 'completed',
      occurred_at: '2027-07-14T16:00:00Z', performed_by: 'Fixture program manager', recorded_by: 'fixture@example.test',
      performed_by_role: null, workflow_stage: '615', artifact_link: null, note: 'Third July activity.', updated_at: null, updated_by: null,
    },
    {
      event_id: 'fixture-july-beta', activity_type: 'beta review', status: 'completed',
      occurred_at: '2027-07-14T09:00:00Z', performed_by: 'Fixture program manager', recorded_by: 'fixture@example.test',
      performed_by_role: null, workflow_stage: '615', artifact_link: null, note: 'Second July activity.', updated_at: null, updated_by: null,
    },
    {
      event_id: 'fixture-july-alpha', activity_type: 'alpha review', status: 'completed',
      occurred_at: '2027-07-14T09:00:00Z', performed_by: 'Fixture program manager', recorded_by: 'fixture@example.test',
      performed_by_role: null, workflow_stage: '615', artifact_link: null, note: 'First July activity.', updated_at: null, updated_by: null,
    },
  ],
};

const contracts = {
  contracts: [{
    flow_id: flowId,
    owner_email: 'fixture@example.test',
    record: {
      contract_number: 'FA-E2E-2026-0001', contract_name: 'Enterprise modernization and support transition',
      contract_status: 'Active', co: 'Fixture contracting officer', cs: 'Fixture contract specialist',
      customer: 'Fixture customer', customer_poc: 'Fixture POC', ktr_poc: 'Fixture contractor POC',
      total_award_value: 1200000, contract_start_date: '2026-01-01', contract_end_date: '2037-12-31',
    },
    option_periods: optionPeriods,
    suspenses,
    activity_events: schedule.activity_events,
  }],
  suspenses,
};

function installFixtureFetch() {
  window.fetch = async (input: RequestInfo | URL) => {
    const endpoint = typeof input === 'string' ? input : input.toString();
    const payload = endpoint.includes(`/contracts/${flowId}/schedule`)
      ? { success: true, data: schedule }
      : endpoint.includes('/api/complex-services/tracker/contracts')
        ? { success: true, data: contracts }
        : { success: false, error: 'Unexpected fixture request.' };
    return new Response(JSON.stringify(payload), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  };
}

/** Browser-only deterministic layout fixture; selected solely by REACT_APP_E2E_FIXTURE. */
export default function ContractScheduleGanttFixture() {
  const [accountAnchorEl, setAccountAnchorEl] = useState<HTMLElement | null>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const closeAccountMenu = () => setAccountAnchorEl(null);
  installFixtureFetch();
  return (
    <ColorModeProvider>
      <ThemeProvider theme={createAppTheme()}>
        <CssBaseline />
        <Box data-testid="e2e-app-shell" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <GlobalNavbar
            userEmail="fixture.contracting.officer@example.gov"
            accountBadgeCount={3}
            unreadWorkflowNotificationCount={1}
            accountAnchorEl={accountAnchorEl}
            accountButtonRef={accountButtonRef}
            onAccountOpen={(event) => setAccountAnchorEl(event.currentTarget)}
            onAccountClose={closeAccountMenu}
            primaryActions={
              <Tooltip title="Training (new module)">
                <IconButton aria-label="Training" color="primary" size="medium">
                  <SchoolOutlinedIcon />
                </IconButton>
              </Tooltip>
            }
            accountMenuItems={[
              'Notifications', 'My feedback', 'User Feedback', 'Usage Dashboard',
              'Manage Users', 'View app as', "What's New", 'Logout',
            ].map((label) => (
              <MenuItem key={label} onClick={closeAccountMenu}>{label}</MenuItem>
            ))}
            onHome={() => {}}
            onManageWorkflows={() => {}}
            onOpenFeedback={() => {}}
          />
          <Box data-testid="e2e-page-content" sx={{ flex: 1, minWidth: 0 }}>
            <ContractDetail contractId={flowId} onBack={() => {}} />
          </Box>
          <Footer />
        </Box>
      </ThemeProvider>
    </ColorModeProvider>
  );
}
